/**
 * Instances (PLAN §5.2): the copies playout runs from. Types only for now —
 * the engine that creates, syncs and times them is a later wave.
 *
 * When a part becomes Next, the engine copies it and its pieces into a
 * PartInstance and PieceInstances. Playout never reads the definition rows
 * again, so an edit or delete of the definition cannot break what is on air
 * (§6.4 layer 2). Instances are owned by the active production process alone
 * and are NOT replicated as rows: they travel on the sequence-numbered
 * engine-state stream (§6.6), which is why they carry no `h`/HLCs.
 *
 * Shapes follow Sofie's `DBPartInstance` / `PieceInstance` (sofie-core 26.x),
 * trimmed to what §5.2–§5.3 use. zod schemas, because the engine-state stream
 * and the UI snapshots cross a process boundary and are parsed like any wire
 * input.
 */
import { z } from 'zod';
import { partFieldsSchema, pieceFieldsSchema } from './fields.js';
import { epochMsSchema, ulidSchema } from './primitives.js';

/**
 * Why an instance no longer matches a definition row (D19): `deleted` — the
 * definition was deleted while the instance was on air or next, and it keeps
 * playing as an orphan; `adlib-part` — an instance created by a queued adlib,
 * with no definition at all.
 */
export const instanceOrphanedSchema = z.enum(['deleted', 'adlib-part']).nullable();
export type InstanceOrphaned = z.infer<typeof instanceOrphanedSchema>;

/** Playout timings of a part instance, all epoch ms, null until they happen. */
export const partInstanceTimingsSchema = z
  .object({
    /** When the operator took it. */
    take: epochMsSchema.nullable(),
    /** When the engine scheduled playback to start (absolute timeline time, §7.1). */
    plannedStartedPlayback: epochMsSchema.nullable(),
    /** When the devices reported playback started. */
    reportedStartedPlayback: epochMsSchema.nullable(),
    plannedStoppedPlayback: epochMsSchema.nullable(),
    reportedStoppedPlayback: epochMsSchema.nullable(),
    /** How far into the part playback began (a take into a running part, a resync). */
    playOffset: z.number().int().nonnegative().nullable(),
  })
  .strict();

export const partInstanceSchema = z
  .object({
    instanceId: ulidSchema,
    /** The definition row it was copied from. */
    partId: ulidSchema,
    segmentId: ulidSchema,
    rundownId: ulidSchema,
    /** The playlist activation this instance belongs to; a reset/re-activation starts a new one. */
    activationId: ulidSchema,
    /**
     * The playlist-wide take counter when this instance was taken (Sofie's
     * `takeCount`), null until taken. Orders instances in the as-run record.
     */
    takeCount: z.number().int().nonnegative().nullable(),
    rehearsal: z.boolean(),
    /** The part's fields as copied (and later synced, §5.2) — never the live row. */
    part: partFieldsSchema,
    orphaned: instanceOrphanedSchema,
    /** Superseded by a fresh copy (re-nexting a played part creates a new instance, §5.2). */
    reset: z.boolean(),
    timings: partInstanceTimingsSchema,
  })
  .strict();
export type PartInstance = z.infer<typeof partInstanceSchema>;

/**
 * Infinite-piece bookkeeping (Sofie's `PieceInstanceInfinite`): a piece whose
 * lifespan outlives its part is carried into following part instances as
 * copies sharing one `infiniteInstanceId`.
 */
export const pieceInstanceInfiniteSchema = z
  .object({
    /** Shared by every copy of one continuing infinite. */
    infiniteInstanceId: ulidSchema,
    /** 0 in the part instance where it started, +1 per continuation. */
    infiniteInstanceIndex: z.number().int().nonnegative(),
    /** The definition piece it started from. */
    infinitePieceId: ulidSchema,
    /** Continued from the previous part instance (rather than starting here). */
    fromPreviousPart: z.boolean(),
    /** Continued from the previous playhead position (a `*-change` lifespan resolved on entry). */
    fromPreviousPlayhead: z.boolean(),
  })
  .strict();
export type PieceInstanceInfinite = z.infer<typeof pieceInstanceInfiniteSchema>;

export const pieceInstanceSchema = z
  .object({
    instanceId: ulidSchema,
    partInstanceId: ulidSchema,
    /** The definition piece, or the adlib it was played from. */
    pieceId: ulidSchema,
    rundownId: ulidSchema,
    /** The piece's fields as copied. */
    piece: pieceFieldsSchema,
    /** Set when an adlib was played into a running part: when (epoch ms), and from which adlib row. */
    dynamicallyInserted: z.object({ time: epochMsSchema, adlibId: ulidSchema }).strict().nullable(),
    infinite: pieceInstanceInfiniteSchema.nullable(),
    plannedStartedPlayback: epochMsSchema.nullable(),
    reportedStartedPlayback: epochMsSchema.nullable(),
    plannedStoppedPlayback: epochMsSchema.nullable(),
    reportedStoppedPlayback: epochMsSchema.nullable(),
    /** Stopped early by an operator (`stopPiecesOnLayer`, §5.3): the effective end, epoch ms. */
    userStopped: epochMsSchema.nullable(),
    /** Skipped by `disableNextPiece` (§5.3). */
    disabled: z.boolean(),
    orphaned: instanceOrphanedSchema,
    reset: z.boolean(),
  })
  .strict();
export type PieceInstance = z.infer<typeof pieceInstanceSchema>;
